class BeezfyNewsletterPremium {
  constructor() {
    this.forms = document.querySelectorAll('[data-newsletter-form]');
    this.init();
  }

  init() {
    if (!this.forms.length) return;

    this.forms.forEach(form => {
      this.setupForm(form);
      this.setupAnimations(form);
      this.setupValidation(form);
    });

    this.setupIntersectionObserver();
  }

  setupForm(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');

    if (!emailInput || !submitButton) return;

    emailInput.addEventListener('focus', (e) => {
      emailInput.parentElement.classList.add('focused');

      // Mobile: scroll input into view
      if (window.innerWidth < 750) {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    });

    emailInput.addEventListener('blur', () => {
      if (!emailInput.value) {
        emailInput.parentElement.classList.remove('focused');
      }
    });

    form.addEventListener('submit', (e) => {
      if (!this.validateForm(form)) {
        e.preventDefault();
        this.showError(form, window.theme?.strings?.newsletter_error || 'Please enter a valid email address');
        return;
      }

      submitButton.classList.add('loading');
      submitButton.disabled = true;

      const originalText = submitButton.querySelector('.beezfy-newsletter__submit-text');
      if (originalText) {
        originalText.dataset.original = originalText.textContent;
        originalText.textContent = 'Subscribing...';
      }

      // Re-enable after response (handled by Shopify form response)
      setTimeout(() => {
        submitButton.classList.remove('loading');
        submitButton.disabled = false;
        if (originalText && originalText.dataset.original) {
          originalText.textContent = originalText.dataset.original;
        }
      }, 3000);
    });
  }

  setupAnimations(form) {
    const inputs = form.querySelectorAll('input');

    inputs.forEach((input, index) => {
      input.style.animationDelay = `${index * 0.1}s`;
    });

    const benefits = form.closest('section')?.querySelectorAll('.beezfy-newsletter__benefit');
    benefits?.forEach((benefit, index) => {
      benefit.style.animationDelay = `${index * 0.05}s`;
    });
  }

  setupValidation(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const nameInputs = form.querySelectorAll('input[type="text"]');
    const privacyCheckbox = form.querySelector('input[name="privacy_accepted"]');

    emailInput?.addEventListener('input', () => {
      if (this.validateEmail(emailInput.value)) {
        emailInput.classList.remove('error');
        emailInput.classList.add('valid');
      } else if (emailInput.value) {
        emailInput.classList.add('error');
        emailInput.classList.remove('valid');
      } else {
        emailInput.classList.remove('error', 'valid');
      }
    });

    nameInputs.forEach(input => {
      input.addEventListener('input', () => {
        if (input.value.length > 1) {
          input.classList.add('valid');
          input.classList.remove('error');
        } else if (input.value) {
          input.classList.add('error');
          input.classList.remove('valid');
        } else {
          input.classList.remove('error', 'valid');
        }
      });
    });

    if (privacyCheckbox) {
      privacyCheckbox.addEventListener('change', () => {
        const label = privacyCheckbox.closest('label');
        if (privacyCheckbox.checked) {
          label?.classList.add('checked');
        } else {
          label?.classList.remove('checked');
        }
      });
    }
  }

  validateForm(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const privacyCheckbox = form.querySelector('input[name="privacy_accepted"]');

    if (!emailInput) return false;

    if (!this.validateEmail(emailInput.value)) {
      emailInput.classList.add('error');
      emailInput.focus();
      return false;
    }

    if (privacyCheckbox && !privacyCheckbox.checked) {
      privacyCheckbox.closest('label')?.classList.add('error');
      return false;
    }

    return true;
  }

  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  showError(form, message) {
    const messagesContainer = form.querySelector('.beezfy-newsletter__messages');
    if (!messagesContainer) return;

    const errorDiv = document.createElement('div');
    errorDiv.className = 'beezfy-newsletter__error';
    errorDiv.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15h-2v-2h2v2zm0-4h-2V5h2v6z"/>
      </svg>
      ${message}
    `;

    messagesContainer.innerHTML = '';
    messagesContainer.appendChild(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  setupIntersectionObserver() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');

          const benefits = entry.target.querySelectorAll('.beezfy-newsletter__benefit');
          benefits.forEach((benefit, index) => {
            setTimeout(() => {
              benefit.classList.add('animate');
            }, index * 100);
          });
        }
      });
    }, observerOptions);

    document.querySelectorAll('.beezfy-newsletter-premium').forEach(section => {
      observer.observe(section);
    });
  }

  trackEvent(eventName, data = {}) {
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish(eventName, data);
    }

    if (window.gtag) {
      window.gtag('event', eventName, data);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new BeezfyNewsletterPremium();
});

class BeezfyNewsletterPopup {
  constructor() {
    this.popup = null;
    this.cookieName = 'beezfy_newsletter_shown';
    this.delay = 5000;
    this.init();
  }

  init() {
    if (this.hasSeenPopup()) return;

    setTimeout(() => {
      this.showPopup();
    }, this.delay);
  }

  hasSeenPopup() {
    return document.cookie.includes(this.cookieName);
  }

  setCookie() {
    const days = 7;
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${this.cookieName}=true;expires=${date.toUTCString()};path=/`;
  }

  showPopup() {
    const newsletterSection = document.querySelector('.beezfy-newsletter-premium');
    if (!newsletterSection || newsletterSection.dataset.popup !== 'true') return;

    const clone = newsletterSection.cloneNode(true);
    clone.classList.add('beezfy-newsletter-popup');

    const overlay = document.createElement('div');
    overlay.className = 'beezfy-newsletter-popup-overlay';

    const closeButton = document.createElement('button');
    closeButton.className = 'beezfy-newsletter-popup-close';
    closeButton.innerHTML = '×';
    closeButton.onclick = () => this.closePopup();

    clone.appendChild(closeButton);
    document.body.appendChild(overlay);
    document.body.appendChild(clone);

    this.popup = clone;
    this.overlay = overlay;

    setTimeout(() => {
      clone.classList.add('active');
      overlay.classList.add('active');
    }, 100);

    overlay.addEventListener('click', () => this.closePopup());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closePopup();
    });
  }

  closePopup() {
    if (!this.popup) return;

    this.popup.classList.remove('active');
    this.overlay.classList.remove('active');

    setTimeout(() => {
      this.popup?.remove();
      this.overlay?.remove();
    }, 300);

    this.setCookie();
  }
}

if (document.querySelector('[data-newsletter-popup="true"]')) {
  new BeezfyNewsletterPopup();
}